import "../global.css";
import { Drawer } from "expo-router/drawer";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import {
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { usePathname } from "expo-router";
import { View, Pressable, Platform } from "react-native";
import {
  ActiveAgentProvider,
  useActiveAgent,
} from "../contexts/ActiveAgentContext";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState, useMemo } from "react";
import { PostHogProviderWrapper } from "../providers/PostHogProviderWrapper";
import { ToastProvider, useToast } from "../contexts/ToastContext";
import { ToastNotification } from "../components/common/toast-notification";
import {
  SettingsDataProvider,
  useSettingsData,
} from "../contexts/SettingsDataContext";
import { useAgentsStatus } from "../hooks/useAgentsStatus";
import { AudioPlayerProvider } from "../contexts/AudioPlayerContext";
import { isUserAdmin, isUserAgentEditor } from "../utils/roles";
import CrispSupportScript from "../components/common/crisp-support-script";
import DrawerContent from "../components/drawer-content";
import AgentSelector from "../components/feature/header/agent-selector/agent-selector";
import { DrawerProvider, useDrawer } from "../contexts/DrawerContext";
import { RequiredOrganizationModal } from "../components/common/required-organization-modal";
import TestCallActiveBanner from "../components/feature/settings/common/test-call-banner";
import { Menu } from "lucide-react-native";
import { StripeProvider } from "../providers/StripeProvider";
import { StripeClientSecretProvider } from "../contexts/StripeClientSecretContext";
import * as Sentry from "@sentry/react-native";
import { useViewport } from "../hooks/useViewport";

Sentry.init({
  dsn: "https://76ae640727eb4268aee7941e7c5664b0@o4507049145073664.ingest.us.sentry.io/4508263501660160",
  sendDefaultPii: true,
  release: process.env.EXPO_PUBLIC_GIT_SHA || "unknown",
  enableLogs: true,
  enabled: !__DEV__,
  spotlight: __DEV__,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Platform.OS === "web"
      ? Sentry.browserReplayIntegration()
      : Sentry.mobileReplayIntegration(),
  ],
});

const queryClient = new QueryClient();

const AuthenticatedLayout = () => {
  const { isSignedIn, user } = useUser();
  const { isLoaded: isLoadedOrgList, userMemberships: orgs } =
    useOrganizationList({
      userMemberships: true,
    });
  const { organization: activeOrg } = useOrganization();
  const [showOrgModal, setShowOrgModal] = useState(false);
  const posthog = usePostHog();
  const pathname = usePathname();
  const { isCollapsed } = useDrawer();
  const { isMobile } = useViewport();

  // require active org
  useEffect(() => {
    const setDefaultOrganization = async () => {
      if (isSignedIn && user) {
        // show modal when no org is selected
        if (isLoadedOrgList && !activeOrg) {
          setShowOrgModal(true);
        }
        // hide modal when org is selected
        if (activeOrg) {
          setShowOrgModal(false);
        }
      }
    };

    setDefaultOrganization();
  }, [isSignedIn, user, isLoadedOrgList, orgs, activeOrg]);

  // posthog identify
  useEffect(() => {
    if (isSignedIn && user) {
      posthog.identify(user.id, {
        email: user.emailAddresses[0]?.emailAddress,
      });
    }
    // reset on logout, on auth layout unmount
    return () => {
      posthog.reset();
    };
  }, [isSignedIn, user]);

  // screens
  useEffect(() => {
    posthog.capture("$pageview", { page: pathname });
  }, [pathname]);

  const { activeAgent } = useActiveAgent();
  const { loadSettings } = useSettingsData();
  useEffect(() => {
    if (activeAgent?.id) {
      loadSettings();
    }
  }, [activeAgent?.id]);

  // Set Crisp user email
  useEffect(() => {
    if (
      isSignedIn &&
      user?.emailAddresses[0]?.emailAddress &&
      typeof window !== "undefined" &&
      window.$crisp
    ) {
      window.$crisp.push([
        "set",
        "user:email",
        user.emailAddresses[0].emailAddress,
      ]);
    }
  }, [isSignedIn, user?.emailAddresses[0]?.emailAddress]);

  const isAdmin = isUserAdmin(user);
  const isAgentEditor = isUserAgentEditor(user);
  const agentsStatus = useAgentsStatus();
  const hasAgentsStatus = !!agentsStatus && !isAdmin && !isAgentEditor;
  if (hasAgentsStatus) {
    return agentsStatus;
  }

  return (
    <StripeProvider>
      <GestureHandlerRootView className="flex-1">
        <View className="flex-1 flex-row">
          <Drawer
            drawerContent={DrawerContent}
            screenOptions={{
              drawerStyle: {
                width: isCollapsed ? 80 : 300,
              },
              headerShown: isMobile,
              drawerType: isMobile ? "front" : "permanent",
              header: ({ navigation, route }) => (
                <View className="flex-row items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                  <Pressable
                    onPress={() => navigation.openDrawer()}
                    className="mr-4"
                  >
                    <Menu size={24} color="#000000" />
                  </Pressable>
                  <View className="flex-1 flex flex-row justify-center">
                    <AgentSelector />
                  </View>
                </View>
              ),
            }}
          >
            <Drawer.Screen
              name="index"
              options={{
                title: "Overview",
                drawerLabel: "Overview",
              }}
            />
            <Drawer.Screen
              name="calls-and-orders"
              options={{
                title: "Calls & Orders",
                drawerLabel: "Calls & Orders",
              }}
            />
            <Drawer.Screen
              name="metrics"
              options={{
                title: "Metrics",
                drawerLabel: "Metrics",
              }}
            />
            <Drawer.Screen
              name="earnings"
              options={{
                title: "Earnings",
                drawerLabel: "Earnings",
              }}
            />
            <Drawer.Screen
              name="settings"
              options={{
                title: "Settings",
                drawerLabel: "Settings",
              }}
            />
            <Drawer.Screen
              name="support"
              options={{
                title: "Support",
                drawerLabel: "Support",
              }}
            />
            <Drawer.Screen
              name="profile"
              options={{
                title: "Profile",
                drawerLabel: "Profile",
                headerShown: false,
              }}
            />
            <Drawer.Screen
              name="organization"
              options={{
                title: "Organization",
                drawerLabel: "Organization",
                headerShown: false,
              }}
            />
          </Drawer>
          <TestCallActiveBanner />
          <RequiredOrganizationModal visible={showOrgModal} />
        </View>
      </GestureHandlerRootView>
    </StripeProvider>
  );
};

function ToastWrapper() {
  const { showToast, toastMessage, toastType, setShowToast } = useToast();

  return showToast ? (
    <ToastNotification
      message={toastMessage}
      type={toastType}
      duration={3000}
      onHide={() => setShowToast(false)}
    />
  ) : null;
}

const Layout = () => {
  const publishableKey = process.env.EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error(
      "Missing Publishable Key. Please set EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY in your .env"
    );
  }

  return (
    <PostHogProviderWrapper>
      <ToastProvider>
        <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
          <SignedIn>
            <QueryClientProvider client={queryClient}>
              <DrawerProvider>
                <ActiveAgentProvider>
                  <SettingsDataProvider>
                    <StripeClientSecretProvider>
                      <AudioPlayerProvider>
                        <AuthenticatedLayout />
                        <ToastWrapper />
                      </AudioPlayerProvider>
                    </StripeClientSecretProvider>
                  </SettingsDataProvider>
                </ActiveAgentProvider>
              </DrawerProvider>
            </QueryClientProvider>
          </SignedIn>
          <SignedOut>
            <RedirectToSignIn />
          </SignedOut>
        </ClerkProvider>
      </ToastProvider>
      <CrispSupportScript />
    </PostHogProviderWrapper>
  );
};

export default Sentry.wrap(Layout);
